import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  loadBossMasterCatalog,
  updateBossMasterCatalogEntry,
} from '../api/bossMasterSyncApi.js';
import {
  bossMasterTriStateLabel,
  buildBossMasterCatalogUpdate,
  createBossMasterCatalogDraft,
  getBossSelectionKey,
} from '../domain/bossMasterCatalog.js';

const TRI_STATE_OPTIONS = [
  ['', '미확인'],
  ['true', '예'],
  ['false', '아니오'],
];

function TriStateField({ label, value, onChange }) {
  return (
    <label>
      <span>{label}</span>
      <select value={value} onChange={(event) => onChange(event.target.value)}>
        {TRI_STATE_OPTIONS.map(([optionValue, optionLabel]) => (
          <option key={optionValue || 'unknown'} value={optionValue}>{optionLabel}</option>
        ))}
      </select>
    </label>
  );
}

function formatVerifiedAt(value) {
  if (!value) return '미검증';
  try {
    return new Intl.DateTimeFormat('ko-KR', {
      year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit',
    }).format(new Date(value));
  } catch {
    return '확인 불가';
  }
}

function CatalogList({ rows, selectedKey, onSelect, onEdit }) {
  if (!rows.length) return <p className="boss-master-catalog-empty">조건에 맞는 보스가 없습니다.</p>;
  return (
    <div className="boss-master-catalog-list" role="list">
      {rows.map((boss) => (
        <button
          type="button"
          key={getBossSelectionKey(boss)}
          className={selectedKey === getBossSelectionKey(boss) ? 'selected' : ''}
          onClick={() => onSelect(getBossSelectionKey(boss))}
          onDoubleClick={() => onEdit(getBossSelectionKey(boss))}
        >
          <span className="boss-master-catalog-thumb">
            {boss.display_image_url ? <img src={boss.display_image_url} alt="" /> : <span>이미지 없음</span>}
          </span>
          <span className="boss-master-catalog-main">
            <strong>{boss.canonical_name}</strong>
            <small>{boss.icon_id || 'icon ID 없음'} · {boss.source_monster_model_id || 'model ID 없음'}</small>
            {boss.aliases.length ? <em>{boss.aliases.join(', ')}</em> : null}
          </span>
          <span className={`boss-master-catalog-verified ${boss.is_verified ? 'verified' : ''}`}>
            {boss.is_verified ? '검증됨' : '미검증'}
          </span>
        </button>
      ))}
    </div>
  );
}

export function BossMasterCatalogPanel({ view, onOpenEditor }) {
  const [rows, setRows] = useState([]);
  const [selectedKey, setSelectedKey] = useState('');
  const [draft, setDraft] = useState(null);
  const [query, setQuery] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [notice, setNotice] = useState({ type: '', message: '' });

  const refresh = useCallback(async () => {
    setLoading(true);
    setNotice({ type: '', message: '' });
    try {
      const nextRows = await loadBossMasterCatalog();
      setRows(nextRows);
      setSelectedKey((current) => current && nextRows.some((row) => getBossSelectionKey(row) === current)
        ? current
        : getBossSelectionKey(nextRows[0]));
    } catch (error) {
      setNotice({ type: 'error', message: error?.message || '보스 목록을 불러오지 못했습니다.' });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  const selected = useMemo(
    () => rows.find((row) => getBossSelectionKey(row) === selectedKey) || null,
    [rows, selectedKey],
  );

  useEffect(() => {
    setDraft(selected ? createBossMasterCatalogDraft(selected) : null);
  }, [selected]);

  const filteredRows = useMemo(() => {
    const keyword = query.trim().toLocaleLowerCase('ko-KR');
    if (!keyword) return rows;
    return rows.filter((boss) => [
      boss.canonical_name,
      boss.icon_id,
      boss.source_boss_id,
      boss.source_monster_model_id,
      ...boss.aliases,
    ].some((value) => String(value || '').toLocaleLowerCase('ko-KR').includes(keyword)));
  }, [query, rows]);

  const openEditor = (key) => {
    setSelectedKey(key);
    onOpenEditor();
  };

  const updateDraft = (field, value) => setDraft((current) => ({ ...current, [field]: value }));

  const save = async () => {
    if (!draft?.id || saving) return;
    setSaving(true);
    setNotice({ type: '', message: '' });
    try {
      const saved = await updateBossMasterCatalogEntry(draft.id, buildBossMasterCatalogUpdate(draft));
      setRows((current) => current.map((row) => row.id === saved.id ? saved : row));
      setNotice({ type: 'success', message: '보스 사실 정보를 저장했습니다.' });
      window.dispatchEvent(new CustomEvent('dororaid:boss-catalog-changed'));
    } catch (error) {
      setNotice({ type: 'error', message: error?.message || '보스 정보를 저장하지 못했습니다.' });
    } finally {
      setSaving(false);
    }
  };

  if (view === 'list') {
    return (
      <section className="boss-master-catalog-panel" aria-label="보스 목록">
        <div className="boss-master-catalog-toolbar">
          <label><span>보스 검색</span><input value={query} onChange={(event) => setQuery(event.target.value)} placeholder="이름, 별명, ID" /></label>
          <div><span>{loading ? '불러오는 중' : `${filteredRows.length} / ${rows.length}개`}</span><button type="button" onClick={refresh} disabled={loading}>새로고침</button></div>
        </div>
        <CatalogList rows={filteredRows} selectedKey={selectedKey} onSelect={setSelectedKey} onEdit={openEditor} />
        {selected ? (
          <div className="boss-master-catalog-selection">
            <div><strong>{selected.canonical_name}</strong><span>코어 {bossMasterTriStateLabel(selected.has_core)} · 부위 {bossMasterTriStateLabel(selected.has_parts)}</span></div>
            <button type="button" onClick={() => openEditor(selected.id)}>상세 편집</button>
          </div>
        ) : null}
        {notice.message ? <p className={`boss-master-sync-notice ${notice.type}`} role="status">{notice.message}</p> : null}
      </section>
    );
  }

  return (
    <section className="boss-master-catalog-panel" aria-label="보스 상세 편집">
      {!draft ? <p className="boss-master-catalog-empty">편집할 보스를 목록에서 선택해주세요.</p> : (
        <>
          <header className="boss-master-editor-header">
            <div>
              <span>CANONICAL BOSS</span>
              <h3>{draft.canonical_name}</h3>
              <p>{draft.icon_id} · 동기화 원본명 {draft.boss_name || '없음'}</p>
            </div>
            {draft.display_image_url ? <img src={draft.display_image_url} alt={`${draft.canonical_name} 대표`} /> : null}
          </header>
          <div className="boss-master-editor-grid">
            <label className="wide"><span>한국어 표시명</span><input value={draft.canonical_name} onChange={(event) => updateDraft('canonical_name', event.target.value)} /></label>
            <label className="wide"><span>원본 / 영문명</span><input value={draft.imported_names.join(', ')} readOnly /></label>
            <label className="wide"><span>대표 이미지 URL</span><input value={draft.boss_image_url} onChange={(event) => updateDraft('boss_image_url', event.target.value)} />{draft.display_image_source === 'boss_image_db' && !draft.boss_image_url ? <small>기존 보스 이미지 DB의 대표 이미지를 표시 중입니다.</small> : null}</label>
            <label><span>원본 boss ID</span><input value={draft.source_boss_id} onChange={(event) => updateDraft('source_boss_id', event.target.value)} /></label>
            <label><span>원본 model ID</span><input value={draft.source_monster_model_id} onChange={(event) => updateDraft('source_monster_model_id', event.target.value)} /></label>
            <label className="wide"><span>별명</span><input value={draft.aliasesText} onChange={(event) => updateDraft('aliasesText', event.target.value)} placeholder="쉼표로 구분" /></label>
            <TriStateField label="코어 존재" value={draft.has_core} onChange={(value) => updateDraft('has_core', value)} />
            <TriStateField label="코어 관통 가능" value={draft.core_pierceable} onChange={(value) => updateDraft('core_pierceable', value)} />
            <TriStateField label="부위 존재" value={draft.has_parts} onChange={(value) => updateDraft('has_parts', value)} />
            <label><span>부위 수</span><input type="number" min="0" value={draft.part_count} disabled={draft.has_parts === 'false'} onChange={(event) => updateDraft('part_count', event.target.value)} /></label>
            <TriStateField label="부위 관통 가능" value={draft.parts_pierceable} onChange={(value) => updateDraft('parts_pierceable', value)} />
            <label className="wide"><span>Mechanic tags</span><input value={draft.mechanicTagsText} onChange={(event) => updateDraft('mechanicTagsText', event.target.value)} placeholder="관통, 파츠 재생" /></label>
            <label className="wide"><span>Notes</span><textarea value={draft.notes} onChange={(event) => updateDraft('notes', event.target.value)} /></label>
            <label className="boss-master-editor-check wide"><input type="checkbox" checked={draft.is_verified} onChange={(event) => updateDraft('is_verified', event.target.checked)} /><span>플랫폼 검증 완료</span><small>검증된 사실 값은 다음 Enikk 동기화에서도 우선 유지됩니다.</small></label>
          </div>
          <div className="boss-master-editor-provenance">
            <div><span>사실 출처</span><strong>{draft.fact_source}</strong></div>
            <div><span>마지막 검증</span><strong>{formatVerifiedAt(draft.verified_at)}</strong></div>
          </div>
          <div className="boss-master-editor-actions">
            {!draft.catalog_schema_ready ? <span>확장 스키마 반영 전에는 목록만 조회할 수 있습니다.</span> : null}
            <button type="button" onClick={save} disabled={saving || !draft.catalog_schema_ready}>{saving ? '저장 중…' : '사실 정보 저장'}</button>
          </div>
        </>
      )}
      {notice.message ? <p className={`boss-master-sync-notice ${notice.type}`} role="status">{notice.message}</p> : null}
    </section>
  );
}
